import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { sbFetch } from "../utils/supabaseClient";
import { SVG_ICONS } from "./TablerIconPicker";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import "../styles/CreateTab.css";

const UsersTab = forwardRef((props, ref) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [updating, setUpdating] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [activeDetailSection, setActiveDetailSection] = useState("overview");
  const [timeRange, setTimeRange] = useState(7);
  const [wordCloudData, setWordCloudData] = useState([]);
  // email -> { enabled, expires_at }. Demo grants live in demo_accounts, which
  // the anon key cannot reach, so they come from the admin API rather than sbFetch.
  const [demoAccounts, setDemoAccounts] = useState({});
  const contentRef = React.useRef(null);

  // Scroll to top when menu section changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [activeDetailSection]);

  // Load word cloud data when user details or time range changes
  useEffect(() => {
    if (userDetails && activeDetailSection === "metrics") {
      loadUserWordCloudData();
    }
  }, [userDetails, timeRange, activeDetailSection]);

  useEffect(() => {
    loadUsers();
    loadDemoAccounts();
  }, []);

  useImperativeHandle(ref, () => ({
    refreshData: () => {
      loadUsers();
      loadDemoAccounts();
    }
  }), []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await sbFetch(
        "GET",
        "registrations"
      );
      console.log("Users data:", data);
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading users:", err);
      alert("Error loading users: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDemoAccounts = async () => {
    try {
      const res = await fetch("/api/demo-accounts");
      if (!res.ok) throw new Error(`Demo accounts request failed: ${res.status}`);
      const data = await res.json();
      const byEmail = {};
      (data.accounts || []).forEach((row) => {
        byEmail[(row.email || "").toLowerCase()] = {
          enabled: row.enabled,
          expires_at: row.expires_at,
        };
      });
      setDemoAccounts(byEmail);
    } catch (err) {
      // A demo grant is an edge case; the rest of the tab still works without it.
      console.error("Error loading demo accounts:", err);
    }
  };

  const isDemoActive = (userEmail) => {
    const grant = demoAccounts[(userEmail || "").toLowerCase()];
    if (!grant || !grant.enabled) return false;
    // The worker treats a lapsed grant as no grant, so the toggle should too.
    if (grant.expires_at && new Date(grant.expires_at).getTime() <= Date.now()) return false;
    return true;
  };

  const handleToggleDemo = async (userEmail, currentState) => {
    const key = (userEmail || "").toLowerCase();
    setUpdating(userEmail);
    try {
      const res = await fetch("/api/demo-accounts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: key, enabled: !currentState }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.error || `Request failed: ${res.status}`);
      }
      const saved = await res.json();
      setDemoAccounts((prev) => ({
        ...prev,
        [key]: { enabled: saved.enabled, expires_at: saved.expires_at },
      }));
    } catch (err) {
      console.error("Error updating demo account:", err);
      alert("Failed to update demo access: " + err.message);
    } finally {
      setUpdating(null);
    }
  };

  const handleToggleOri = async (userEmail, currentState) => {
    setUpdating(userEmail);
    try {
      await sbFetch(
        "PATCH",
        `registrations?email=eq.${encodeURIComponent(userEmail)}`,
        { ori_enabled: !currentState },
        { Prefer: "return=minimal" }
      );

      setUsers(
        users.map((u) =>
          u.email === userEmail ? { ...u, ori_enabled: !currentState } : u
        )
      );
    } catch (err) {
      console.error("Error updating user:", err);
      alert("Failed to update user");
    } finally {
      setUpdating(null);
    }
  };

  const handleDeleteUser = async (userEmail, userName) => {
    if (!window.confirm(`Remove ${userName || userEmail} from the system?`)) {
      return;
    }

    setUpdating(userEmail);
    try {
      await sbFetch(
        "DELETE",
        `registrations?email=eq.${encodeURIComponent(userEmail)}`,
        null,
        { Prefer: "return=minimal" }
      );

      setUsers(users.filter((u) => u.email !== userEmail));
    } catch (err) {
      console.error("Error deleting user:", err);
      alert("Failed to delete user");
    } finally {
      setUpdating(null);
    }
  };

  const loadUserDetails = async (user) => {
    setSelectedUser(user);
    try {
      // Get full user record from registrations
      const userRecord = await sbFetch(
        "GET",
        `registrations?email=eq.${encodeURIComponent(user.email)}`
      );

      const userDetails = userRecord?.[0] || user;

      // Fetch completed lessons from lessons_completed table
      try {
        const completedLessons = await sbFetch(
          "GET",
          `lessons_completed?email=eq.${encodeURIComponent(user.email)}`
        );
        userDetails.lessons_completed = Array.isArray(completedLessons) ? completedLessons : [];
      } catch (err) {
        console.error("Error loading lessons completed:", err);
        userDetails.lessons_completed = [];
      }

      // Fetch copied prompts from prompt_copies table
      try {
        const copiedPrompts = await sbFetch(
          "GET",
          `prompt_copies?email=eq.${encodeURIComponent(user.email)}`
        );
        userDetails.prompts_copied = Array.isArray(copiedPrompts) ? copiedPrompts : [];
      } catch (err) {
        console.error("Error loading prompts copied:", err);
        userDetails.prompts_copied = [];
      }

      // Fetch Ori questions from ori_usage table
      try {
        const oriQuestions = await sbFetch(
          "GET",
          `ori_usage?email=eq.${encodeURIComponent(user.email)}&order=sent_at.desc`
        );
        userDetails.ori_questions = Array.isArray(oriQuestions) ? oriQuestions : [];
      } catch (err) {
        console.error("Error loading ori questions:", err);
        userDetails.ori_questions = [];
      }

      setUserDetails(userDetails);
    } catch (err) {
      console.error("Error loading user details:", err);
      setUserDetails(user);
    }
  };

  const getFilteredMetrics = () => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - timeRange);

    const lessonsInRange = (userDetails.lessons_completed || []).filter((lesson) => {
      const lessonDate = lesson.completed_at ? new Date(lesson.completed_at) : null;
      return lessonDate && lessonDate >= cutoffDate;
    });

    const promptsInRange = (userDetails.prompts_copied || []).filter((prompt) => {
      const promptDate = prompt.copied_at ? new Date(prompt.copied_at) : null;
      return promptDate && promptDate >= cutoffDate;
    });

    const oriInRange = (userDetails.ori_questions || []).filter((item) => {
      const itemDate = item.sent_at || item.created_at;
      const date = itemDate ? new Date(itemDate) : null;
      return date && date >= cutoffDate;
    });

    return {
      lessons: lessonsInRange.length,
      prompts: promptsInRange.length,
      ori: oriInRange.length,
      interactions: lessonsInRange.length + promptsInRange.length + oriInRange.length,
    };
  };

  const getInteractionChartData = () => {
    const data = [];
    const days = timeRange;
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - timeRange);

    // Create day buckets
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const day = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);

      const entry = {
        day,
        lesson_completed: 0,
        prompt_copied: 0,
        resource_accessed: 0,
        ori_message: 0,
      };

      // Count lessons on this day
      (userDetails.lessons_completed || []).forEach((lesson) => {
        const lessonDate = lesson.completed_at ? new Date(lesson.completed_at) : null;
        if (lessonDate && lessonDate >= startOfDay && lessonDate <= endOfDay) {
          entry.lesson_completed++;
        }
      });

      // Count prompts on this day
      (userDetails.prompts_copied || []).forEach((prompt) => {
        const promptDate = prompt.copied_at ? new Date(prompt.copied_at) : null;
        if (promptDate && promptDate >= startOfDay && promptDate <= endOfDay) {
          entry.prompt_copied++;
        }
      });

      // Count Ori messages on this day
      (userDetails.ori_questions || []).forEach((item) => {
        const itemDate = item.sent_at || item.created_at;
        const date = itemDate ? new Date(itemDate) : null;
        if (date && date >= startOfDay && date <= endOfDay) {
          entry.ori_message++;
        }
      });

      data.push(entry);
    }
    return data;
  };

  const loadUserWordCloudData = async () => {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - timeRange);
      const now = new Date();

      // Fetch content definitions
      const [resourcesContent, lessonsContent, promptsContent] = await Promise.all([
        sbFetch("GET", "app_settings?key=eq.origamic-resources").catch(() => []),
        sbFetch("GET", "app_settings?key=eq.origamic-lessons").catch(() => []),
        sbFetch("GET", "app_settings?key=eq.origamic-prompts").catch(() => [])
      ]);

      // Parse content
      let resources = [];
      if (Array.isArray(resourcesContent) && resourcesContent.length > 0) {
        try {
          resources = JSON.parse(resourcesContent[0].value) || [];
        } catch (e) {
          resources = [];
        }
      }

      let lessons = [];
      if (Array.isArray(lessonsContent) && lessonsContent.length > 0) {
        try {
          lessons = JSON.parse(lessonsContent[0].value) || [];
        } catch (e) {
          lessons = [];
        }
      }

      let prompts = [];
      if (Array.isArray(promptsContent) && promptsContent.length > 0) {
        try {
          prompts = JSON.parse(promptsContent[0].value) || [];
        } catch (e) {
          prompts = [];
        }
      }

      // Create lookup maps
      const resourceMap = {};
      resources.forEach(r => {
        resourceMap[r.id] = r.category;
      });

      const lessonMap = {};
      lessons.forEach(l => {
        lessonMap[l.id] = l.tag;
      });

      const promptMap = {};
      prompts.forEach(p => {
        promptMap[p.id] = p.cat;
      });

      // Count categories from user interactions
      const categoryCounts = {};

      // Filter lessons in range
      const lessonsInRange = (userDetails.lessons_completed || []).filter((lesson) => {
        const lessonDate = lesson.completed_at ? new Date(lesson.completed_at) : null;
        return lessonDate && lessonDate >= cutoffDate && lessonDate <= now;
      });

      lessonsInRange.forEach(item => {
        const category = lessonMap[item.lesson_id];
        if (category) {
          categoryCounts[category] = (categoryCounts[category] || 0) + 1;
        }
      });

      // Filter prompts in range
      const promptsInRange = (userDetails.prompts_copied || []).filter((prompt) => {
        const promptDate = prompt.copied_at ? new Date(prompt.copied_at) : null;
        return promptDate && promptDate >= cutoffDate && promptDate <= now;
      });

      promptsInRange.forEach(item => {
        const category = promptMap[item.prompt_id];
        if (category) {
          categoryCounts[category] = (categoryCounts[category] || 0) + 1;
        }
      });

      // Generate word cloud data with sizes
      const maxCount = Math.max(...Object.values(categoryCounts), 1);
      const wordCloudArray = Object.entries(categoryCounts)
        .map(([category, count]) => ({
          category,
          count,
          size: 12 + (count / maxCount) * 32 // Font size between 12 and 44
        }))
        .sort(() => Math.random() - 0.5); // Shuffle for visual variety

      setWordCloudData(wordCloudArray);
    } catch (err) {
      console.error("Error loading user word cloud data:", err);
    }
  };

  const filteredUsers = users.filter((user) =>
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  // Detail view
  if (selectedUser && userDetails) {
    return (
      <div className="create-container" style={{ height: "100vh", display: "flex", flexDirection: "column", padding: 0, maxWidth: "none" }}>
        <div style={{ display: "flex", gap: "0", position: "relative", flex: 1, overflow: "hidden" }}>
          {/* Left Sidebar */}
          <div
            style={{
              position: "fixed",
              left: 0,
              top: 130,
              width: "220px",
              height: "calc(100vh - 130px)",
              background: "rgba(59, 130, 246, 0.05)",
              border: "1px solid rgba(59, 130, 246, 0.15)",
              borderRadius: "0px",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              overflowY: "auto",
              zIndex: 99,
            }}
          >
            {/* User Details Header */}
            <div style={{ marginBottom: "12px", paddingBottom: "12px", borderBottom: "1px solid rgba(59, 130, 246, 0.2)" }}>
              <h3 style={{ margin: "0 0 8px 0", fontSize: "14px", fontWeight: "600", color: "#5B9EF0" }}>
                {userDetails.name || "User Details"}
              </h3>
              <button
                onClick={() => {
                  setSelectedUser(null);
                  setUserDetails(null);
                  setActiveDetailSection("overview");
                }}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  background: "rgba(59, 130, 246, 0.15)",
                  border: "1px solid rgba(59, 130, 246, 0.3)",
                  color: "#5B9EF0",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "600",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => (e.target.style.background = "rgba(59, 130, 246, 0.25)")}
                onMouseLeave={(e) => (e.target.style.background = "rgba(59, 130, 246, 0.15)")}
              >
                ← Back to Users
              </button>
            </div>

            <div style={{ fontSize: "12px", fontWeight: "600", color: "#7A9CC4", marginBottom: "8px", textTransform: "uppercase" }}>
              Navigation
            </div>
            {[
              { id: "overview", label: "Overview", icon: "clipboard-list" },
              { id: "metrics", label: "Metrics", icon: "chart-bar" },
              { id: "compliance", label: "Compliance", icon: "check-circle" },
              { id: "lessons", label: "Lessons", icon: "book" },
              { id: "prompts", label: "Prompts", icon: "message" },
              { id: "ori", label: "Ori Questions", icon: "brain" },
            ].map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveDetailSection(section.id)}
                style={{
                  padding: "10px 12px",
                  background: activeDetailSection === section.id
                    ? "rgba(91, 158, 240, 0.2)"
                    : "transparent",
                  border: activeDetailSection === section.id
                    ? "1px solid rgba(91, 158, 240, 0.4)"
                    : "1px solid transparent",
                  color: activeDetailSection === section.id ? "#5B9EF0" : "#7A9CC4",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: "500",
                  textAlign: "left",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {section.icon && SVG_ICONS[section.icon] && (
                  <span style={{ display: "flex", alignItems: "center" }}>
                    {SVG_ICONS[section.icon]({ size: 16, color: activeDetailSection === section.id ? "#5B9EF0" : "#7A9CC4" })}
                  </span>
                )}
                {section.label}
              </button>
            ))}
          </div>

          {/* Main Content */}
          <div ref={contentRef} style={{ flex: 1, marginLeft: "220px", overflowY: "auto", paddingTop: "30px", paddingRight: "24px", paddingBottom: "24px", paddingLeft: "24px" }}>
            {/* Metrics Section */}
            {activeDetailSection === "metrics" && (
              <div key={`metrics-${timeRange}`}>
                <h3 style={{ color: "#5B9EF0", marginBottom: "16px" }}>User Engagement Metrics</h3>

                {/* Interactions Chart */}
                <div style={{ marginTop: "32px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                    <h4 style={{ color: "#5B9EF0", margin: "0", fontSize: "14px", fontWeight: "600" }}>Interactions Over Time</h4>
                    <button
                      onClick={() => loadUserWordCloudData()}
                      style={{
                        background: "transparent",
                        border: "1px solid rgba(59, 130, 246, 0.3)",
                        borderRadius: "6px",
                        color: "#5B9EF0",
                        cursor: "pointer",
                        padding: "6px 12px",
                        fontSize: "12px",
                        fontWeight: "600",
                        transition: "all 0.2s",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = "rgba(59, 130, 246, 0.1)";
                        e.target.style.borderColor = "rgba(59, 130, 246, 0.5)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = "transparent";
                        e.target.style.borderColor = "rgba(59, 130, 246, 0.3)";
                      }}
                      title="Refresh data"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
                      </svg>
                      Refresh
                    </button>
                  </div>

                  {/* Time Range Filters */}
                  <div style={{ display: "flex", gap: "8px", marginBottom: "24px" }}>
                    {[7, 30, 60, 90].map((range) => (
                      <button
                        key={range}
                        onClick={() => setTimeRange(range)}
                        style={{
                          padding: "8px 16px",
                          background: timeRange === range ? "rgba(59, 130, 246, 0.3)" : "rgba(59, 130, 246, 0.1)",
                          border: timeRange === range ? "1px solid rgba(59, 130, 246, 0.6)" : "1px solid rgba(59, 130, 246, 0.2)",
                          color: timeRange === range ? "#5B9EF0" : "#7A9CC4",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "13px",
                          fontWeight: "500",
                          transition: "all 0.2s",
                        }}
                      >
                        {range} days
                      </button>
                    ))}
                  </div>

                  {/* Chart */}
                  <div style={{ background: "rgba(59, 130, 246, 0.05)", border: "1px solid rgba(59, 130, 246, 0.15)", borderRadius: "8px", padding: "20px" }}>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={getInteractionChartData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(59, 130, 246, 0.2)" />
                        <XAxis dataKey="day" stroke="#7A9CC4" style={{ fontSize: "12px" }} />
                        <YAxis stroke="#7A9CC4" style={{ fontSize: "12px" }} />
                        <Tooltip
                          contentStyle={{ background: "#050e1a", border: "1px solid rgba(59, 130, 246, 0.3)", borderRadius: "6px" }}
                          labelStyle={{ color: "#5B9EF0" }}
                          cursor={{ fill: "transparent" }}
                        />
                        <Legend wrapperStyle={{ paddingTop: "16px" }} />
                        <Bar dataKey="lesson_completed" stackId="a" fill="#0EA5E9" name="Lessons" />
                        <Bar dataKey="prompt_copied" stackId="a" fill="#10B981" name="Prompts" />
                        <Bar dataKey="resource_accessed" stackId="a" fill="#F97316" name="Resources" />
                        <Bar dataKey="ori_message" stackId="a" fill="#8B5CF6" name="Ori Messages" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Chiclets at Bottom */}
                <div style={{ marginTop: "32px", paddingTop: "24px", borderTop: "1px solid rgba(59, 130, 246, 0.2)" }}>
                  <h4 style={{ color: "#5B9EF0", marginBottom: "16px", fontSize: "14px", fontWeight: "600" }}>Summary</h4>
                  <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                    {/* Interactions Chiclet */}
                    <div style={{ padding: "10px 18px", background: "rgba(59, 130, 246, 0.2)", border: "1px solid rgba(59, 130, 246, 0.4)", borderRadius: "20px", display: "flex", alignItems: "center", gap: "8px" }}>
                      <div style={{ fontSize: "13px", color: "#7A9CC4" }}>Interactions:</div>
                      <div style={{ fontSize: "16px", fontWeight: "700", color: "#5B9EF0" }}>{getFilteredMetrics().interactions}</div>
                    </div>
                    {/* Lessons Chiclet */}
                    <div style={{ padding: "10px 18px", background: "rgba(168, 85, 247, 0.2)", border: "1px solid rgba(168, 85, 247, 0.4)", borderRadius: "20px", display: "flex", alignItems: "center", gap: "8px" }}>
                      <div style={{ fontSize: "13px", color: "#7A9CC4" }}>Lessons:</div>
                      <div style={{ fontSize: "16px", fontWeight: "700", color: "#d8b4fe" }}>{getFilteredMetrics().lessons}</div>
                    </div>
                    {/* Prompts Chiclet */}
                    <div style={{ padding: "10px 18px", background: "rgba(34, 197, 94, 0.2)", border: "1px solid rgba(34, 197, 94, 0.4)", borderRadius: "20px", display: "flex", alignItems: "center", gap: "8px" }}>
                      <div style={{ fontSize: "13px", color: "#7A9CC4" }}>Prompts:</div>
                      <div style={{ fontSize: "16px", fontWeight: "700", color: "#86EFAC" }}>{getFilteredMetrics().prompts}</div>
                    </div>
                    {/* Ori Messages Chiclet */}
                    <div style={{ padding: "10px 18px", background: "rgba(168, 85, 247, 0.2)", border: "1px solid rgba(168, 85, 247, 0.4)", borderRadius: "20px", display: "flex", alignItems: "center", gap: "8px" }}>
                      <div style={{ fontSize: "13px", color: "#7A9CC4" }}>Ori Messages:</div>
                      <div style={{ fontSize: "16px", fontWeight: "700", color: "#d8b4fe" }}>{getFilteredMetrics().ori}</div>
                    </div>
                  </div>
                </div>

                {/* Word Cloud - Categories */}
                <div style={{ marginTop: "32px", paddingTop: "24px", borderTop: "1px solid rgba(59, 130, 246, 0.2)" }}>
                  <h4 style={{ color: "#5B9EF0", marginBottom: "16px", fontSize: "14px", fontWeight: "600" }}>Interacted Categories</h4>
                  <div style={{ padding: "20px", background: "rgba(168, 85, 247, 0.08)", border: "1px solid rgba(168, 85, 247, 0.2)", borderRadius: "8px", minHeight: "120px" }}>
                    {wordCloudData && wordCloudData.length > 0 ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
                        {wordCloudData.map((item, idx) => (
                          <span
                            key={idx}
                            style={{
                              fontSize: `${item.size}px`,
                              color: `hsl(${Math.random() * 60 + 200}, 70%, 50%)`,
                              fontWeight: Math.random() > 0.5 ? "600" : "400",
                              opacity: 0.7 + item.count / (Math.max(...wordCloudData.map(w => w.count), 1)) * 0.3,
                              cursor: "default"
                            }}
                          >
                            {item.category}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: "#7A9CC4", textAlign: "center", padding: "20px", fontSize: "13px" }}>No categories in selected period</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Overview Section */}
            {activeDetailSection === "overview" && (
              <div>
                <h3 style={{ color: "#5B9EF0", marginBottom: "16px" }}>User Information</h3>
                <div
                  style={{
                    padding: "20px",
                    background: "rgba(59, 130, 246, 0.08)",
                    border: "1px solid rgba(59, 130, 246, 0.2)",
                    borderRadius: "8px",
                    marginBottom: "24px",
                  }}
                >
                  <div style={{ display: "grid", gap: "12px", fontSize: "13px", color: "#ccc" }}>
                    <div>
                      <span style={{ color: "#5B9EF0", fontWeight: "600" }}>Name:</span> {userDetails.name || "—"}
                    </div>
                    <div>
                      <span style={{ color: "#5B9EF0", fontWeight: "600" }}>Email:</span> {userDetails.email}
                    </div>
                    <div>
                      <span style={{ color: "#5B9EF0", fontWeight: "600" }}>Role:</span> {userDetails.role || "—"}
                    </div>
                    <div>
                      <span style={{ color: "#5B9EF0", fontWeight: "600" }}>Website:</span>{" "}
                      {userDetails.website ? (
                        <a
                          href={userDetails.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#818cf8", textDecoration: "underline" }}
                        >
                          {userDetails.website}
                        </a>
                      ) : (
                        "—"
                      )}
                    </div>
                    <div>
                      <span style={{ color: "#5B9EF0", fontWeight: "600" }}>Ori Enabled:</span>{" "}
                      {userDetails.ori_enabled ? (
                        <span style={{ color: "#86EFAC" }}>✓ Yes</span>
                      ) : (
                        <span style={{ color: "#f87171" }}>✗ No</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Compliance Section */}
            {activeDetailSection === "compliance" && (
              <div>
                <h3 style={{ color: "#5B9EF0", marginBottom: "16px" }}>Compliance & Agreements</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {/* Terms */}
                  <div
                    style={{
                      padding: "16px",
                      background: userDetails.agreed_terms
                        ? "rgba(34, 197, 94, 0.1)"
                        : "rgba(248, 113, 113, 0.1)",
                      border: userDetails.agreed_terms
                        ? "1px solid rgba(34, 197, 94, 0.2)"
                        : "1px solid rgba(248, 113, 113, 0.2)",
                      borderRadius: "8px",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                      <span style={{ fontSize: "18px" }}>
                        {userDetails.agreed_terms ? "✓" : "✗"}
                      </span>
                      <span style={{ fontWeight: "600", color: "#ccc" }}>Terms of Service</span>
                    </div>
                    <div style={{ display: "grid", gap: "4px" }}>
                      {userDetails.updated_at && (
                        <div style={{ fontSize: "12px", color: "#7A9CC4" }}>
                          <span style={{ fontWeight: "600" }}>Last Checked:</span> {new Date(userDetails.updated_at).toLocaleDateString()} at{" "}
                          {new Date(userDetails.updated_at).toLocaleTimeString()}
                        </div>
                      )}
                      {userDetails.agreed_terms_version && (
                        <div style={{ fontSize: "12px", color: "#7A9CC4" }}>
                          <span style={{ fontWeight: "600" }}>Version:</span> {userDetails.agreed_terms_version}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Privacy */}
                  <div
                    style={{
                      padding: "16px",
                      background: userDetails.agreed_privacy
                        ? "rgba(34, 197, 94, 0.1)"
                        : "rgba(248, 113, 113, 0.1)",
                      border: userDetails.agreed_privacy
                        ? "1px solid rgba(34, 197, 94, 0.2)"
                        : "1px solid rgba(248, 113, 113, 0.2)",
                      borderRadius: "8px",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                      <span style={{ fontSize: "18px" }}>
                        {userDetails.agreed_privacy ? "✓" : "✗"}
                      </span>
                      <span style={{ fontWeight: "600", color: "#ccc" }}>Privacy Policy</span>
                    </div>
                    <div style={{ display: "grid", gap: "4px" }}>
                      {userDetails.updated_at && (
                        <div style={{ fontSize: "12px", color: "#7A9CC4" }}>
                          <span style={{ fontWeight: "600" }}>Last Checked:</span> {new Date(userDetails.updated_at).toLocaleDateString()} at{" "}
                          {new Date(userDetails.updated_at).toLocaleTimeString()}
                        </div>
                      )}
                      {userDetails.agreed_privacy_version && (
                        <div style={{ fontSize: "12px", color: "#7A9CC4" }}>
                          <span style={{ fontWeight: "600" }}>Version:</span> {userDetails.agreed_privacy_version}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Lessons Section */}
            {activeDetailSection === "lessons" && (
              <div>
                <h3 style={{ color: "#5B9EF0", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                  {SVG_ICONS["book"] && SVG_ICONS["book"]({ size: 20, color: "#5B9EF0" })}
                  Lessons Completed ({userDetails.lessons_completed?.length || 0})
                </h3>
                {userDetails.lessons_completed && userDetails.lessons_completed.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {userDetails.lessons_completed.map((lesson, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: "12px 14px",
                          background: "rgba(34, 197, 94, 0.1)",
                          border: "1px solid rgba(34, 197, 94, 0.2)",
                          borderRadius: "6px",
                          fontSize: "13px",
                          color: "#86EFAC",
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(34, 197, 94, 0.2)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(34, 197, 94, 0.1)")}
                      >
                        <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                          {lesson.lesson_title || lesson.title || lesson}
                        </div>
                        {lesson.completed_at && (
                          <div style={{ fontSize: "11px", color: "#4ade80", marginTop: "4px" }}>
                            Completed: {new Date(lesson.completed_at).toLocaleDateString()} at{" "}
                            {new Date(lesson.completed_at).toLocaleTimeString()}
                          </div>
                        )}
                        {lesson.lesson_id && (
                          <div style={{ fontSize: "11px", color: "#4ade80", marginTop: "2px" }}>
                            ID: {lesson.lesson_id}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      padding: "24px",
                      textAlign: "center",
                      background: "rgba(59, 130, 246, 0.05)",
                      borderRadius: "8px",
                      color: "#7A9CC4",
                      fontSize: "13px",
                    }}
                  >
                    No lessons completed
                  </div>
                )}
              </div>
            )}

            {/* Prompts Section */}
            {activeDetailSection === "prompts" && (
              <div>
                <h3 style={{ color: "#5B9EF0", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                  {SVG_ICONS["message"] && SVG_ICONS["message"]({ size: 20, color: "#5B9EF0" })}
                  Prompts Copied ({userDetails.prompts_copied?.length || 0})
                </h3>
                {userDetails.prompts_copied && userDetails.prompts_copied.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {userDetails.prompts_copied.map((prompt, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: "12px 14px",
                          background: "rgba(99, 102, 241, 0.1)",
                          border: "1px solid rgba(99, 102, 241, 0.2)",
                          borderRadius: "6px",
                          fontSize: "13px",
                          color: "#818cf8",
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(99, 102, 241, 0.2)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(99, 102, 241, 0.1)")}
                      >
                        <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                          {prompt.prompt_title || prompt.title || prompt}
                        </div>
                        {prompt.copied_at && (
                          <div style={{ fontSize: "11px", color: "#a5b4fc", marginTop: "4px" }}>
                            Copied: {new Date(prompt.copied_at).toLocaleDateString()} at{" "}
                            {new Date(prompt.copied_at).toLocaleTimeString()}
                          </div>
                        )}
                        {prompt.prompt_id && (
                          <div style={{ fontSize: "11px", color: "#a5b4fc", marginTop: "2px" }}>
                            ID: {prompt.prompt_id}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      padding: "24px",
                      textAlign: "center",
                      background: "rgba(59, 130, 246, 0.05)",
                      borderRadius: "8px",
                      color: "#7A9CC4",
                      fontSize: "13px",
                    }}
                  >
                    No prompts copied
                  </div>
                )}
              </div>
            )}

            {/* Ori Questions Section */}
            {activeDetailSection === "ori" && (
              <div>
                <h3 style={{ color: "#5B9EF0", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                  {SVG_ICONS["brain"] && SVG_ICONS["brain"]({ size: 20, color: "#5B9EF0" })}
                  Ori Questions ({userDetails.ori_questions?.length || 0})
                </h3>
                {userDetails.ori_questions && userDetails.ori_questions.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {userDetails.ori_questions.map((item, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: "12px 14px",
                          background: "rgba(168, 85, 247, 0.1)",
                          border: "1px solid rgba(168, 85, 247, 0.2)",
                          borderRadius: "6px",
                          fontSize: "13px",
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(168, 85, 247, 0.2)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(168, 85, 247, 0.1)")}
                      >
                        <div style={{ color: "#d8b4fe", fontWeight: "600", marginBottom: "4px" }}>
                          Q: {typeof item === "object" ? (item.message || item.question || "—") : item}
                        </div>
                        {typeof item === "object" && item.response && (
                          <div style={{ color: "#c4b5fd", fontSize: "12px", marginBottom: "4px", marginTop: "4px" }}>
                            <strong>A:</strong> {item.response.substring(0, 150)}{item.response.length > 150 ? "..." : ""}
                          </div>
                        )}
                        {typeof item === "object" && (item.sent_at || item.created_at) && (
                          <div style={{ color: "#a78bfa", fontSize: "11px", marginTop: "4px" }}>
                            {new Date(item.sent_at || item.created_at).toLocaleString()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      padding: "24px",
                      textAlign: "center",
                      background: "rgba(59, 130, 246, 0.05)",
                      borderRadius: "8px",
                      color: "#7A9CC4",
                      fontSize: "13px",
                    }}
                  >
                    No Ori questions asked
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-container">
      <h2>Users Management</h2>

      <div style={{ width: "100%", maxWidth: "100%" }}>
        {/* Search Bar */}
        <div style={{ marginBottom: "24px" }}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name or email..."
            style={{
              width: "100%",
              padding: "12px",
              background: "#050e1a",
              border: "1px solid rgba(59, 130, 246, 0.15)",
              borderRadius: "8px",
              color: "white",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* Users Count */}
        <div style={{ marginBottom: "16px", fontSize: "13px", color: "#7A9CC4" }}>
          Showing {filteredUsers.length} of {users.length} users
        </div>

        {/* Users Grid */}
        {filteredUsers.length === 0 ? (
          <div
            style={{
              padding: "24px",
              textAlign: "center",
              color: "#7A9CC4",
              background: "rgba(59, 130, 246, 0.05)",
              borderRadius: "8px",
            }}
          >
            No users found
          </div>
        ) : (
          <div style={{ display: "grid", gap: "12px" }}>
            {filteredUsers.map((user) => (
              <div
                key={user.email}
                onClick={() => loadUserDetails(user)}
                style={{
                  padding: "16px",
                  background: "rgba(59, 130, 246, 0.08)",
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(59, 130, 246, 0.15)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(59, 130, 246, 0.08)")}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "16px",
                  }}
                >
                  {/* User Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "600", color: "#5B9EF0", marginBottom: "4px" }}>
                      {user.name || "—"}
                    </div>
                    <div style={{ fontSize: "13px", color: "#ccc", marginBottom: "8px" }}>
                      {user.email}
                    </div>

                    {/* Details */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "12px",
                        fontSize: "12px",
                        color: "#7A9CC4",
                      }}
                    >
                      {user.website && (
                        <div>
                          <span style={{ color: "#5B9EF0", fontWeight: "600" }}>Website:</span>{" "}
                          <a
                            href={user.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#818cf8", textDecoration: "underline" }}
                          >
                            {user.website.replace(/^https?:\/\//, "")}
                          </a>
                        </div>
                      )}
                      {user.role && (
                        <div>
                          <span style={{ color: "#5B9EF0", fontWeight: "600" }}>Role:</span>{" "}
                          {user.role}
                        </div>
                      )}
                      <div>
                        <span style={{ color: "#5B9EF0", fontWeight: "600" }}>Terms:</span>{" "}
                        {user.agreed_terms ? "✓ Accepted" : "✗ Not Accepted"}
                      </div>
                      <div>
                        <span style={{ color: "#5B9EF0", fontWeight: "600" }}>Privacy:</span>{" "}
                        {user.agreed_privacy ? "✓ Accepted" : "✗ Not Accepted"}
                      </div>
                      {user.registered_at && (
                        <div
                          style={{
                            gridColumn: "1 / -1",
                            marginTop: "4px",
                            paddingTop: "8px",
                            borderTop: "1px solid rgba(59, 130, 246, 0.2)",
                          }}
                        >
                          <span style={{ color: "#7A9CC4", fontSize: "11px" }}>
                            Registered {new Date(user.registered_at).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Ori Toggle & Delete */}
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "16px" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                      <div
                        onClick={(e) => {
                          // The whole card opens the detail panel, so a toggle
                          // has to keep its click to itself.
                          e.stopPropagation();
                          handleToggleOri(user.email, user.ori_enabled);
                        }}
                        style={{
                          width: "50px",
                          height: "28px",
                          background: user.ori_enabled ? "#a855f7" : "#6b7280",
                          borderRadius: "20px",
                          position: "relative",
                          cursor: "pointer",
                          transition: "background 0.2s",
                          display: "flex",
                          alignItems: "center",
                          paddingRight: user.ori_enabled ? "4px" : "20px",
                          opacity: updating === user.email ? 0.6 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: "24px",
                            height: "24px",
                            background: "white",
                            borderRadius: "50%",
                            position: "absolute",
                            left: user.ori_enabled ? "23px" : "2px",
                            transition: "left 0.2s",
                          }}
                        />
                      </div>
                      <div style={{ fontSize: "11px", color: "#7A9CC4", textAlign: "center" }}>
                        Ori
                      </div>
                      <div
                        style={{
                          fontSize: "11px",
                          fontWeight: "600",
                          textAlign: "center",
                          marginTop: "-4px",
                          color: user.ori_enabled ? "#a855f7" : "#7A9CC4",
                        }}
                      >
                        {user.ori_enabled ? "On" : "Off"}
                      </div>
                    </div>

                    {/* Demo Account Toggle */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleDemo(user.email, isDemoActive(user.email));
                        }}
                        title="Lets this address sign in with a fixed App Review code instead of an emailed one"
                        style={{
                          width: "50px",
                          height: "28px",
                          background: isDemoActive(user.email) ? "#F0A500" : "#6b7280",
                          borderRadius: "20px",
                          position: "relative",
                          cursor: "pointer",
                          transition: "background 0.2s",
                          display: "flex",
                          alignItems: "center",
                          opacity: updating === user.email ? 0.6 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: "24px",
                            height: "24px",
                            background: "white",
                            borderRadius: "50%",
                            position: "absolute",
                            left: isDemoActive(user.email) ? "23px" : "2px",
                            transition: "left 0.2s",
                          }}
                        />
                      </div>
                      <div style={{ fontSize: "11px", color: "#7A9CC4", textAlign: "center" }}>
                        Demo
                      </div>
                      <div
                        style={{
                          fontSize: "11px",
                          fontWeight: "600",
                          textAlign: "center",
                          marginTop: "-4px",
                          color: isDemoActive(user.email) ? "#F0A500" : "#7A9CC4",
                        }}
                      >
                        {isDemoActive(user.email) ? "On" : "Off"}
                      </div>
                      {isDemoActive(user.email) &&
                        demoAccounts[(user.email || "").toLowerCase()]?.expires_at && (
                          <div style={{ fontSize: "10px", color: "#F0A500", textAlign: "center" }}>
                            until{" "}
                            {new Date(
                              demoAccounts[(user.email || "").toLowerCase()].expires_at
                            ).toLocaleDateString()}
                          </div>
                        )}
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteUser(user.email, user.name);
                      }}
                      style={{
                        padding: "6px 12px",
                        background: "rgba(248, 113, 113, 0.15)",
                        border: "1px solid rgba(248, 113, 113, 0.3)",
                        color: "#f87171",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "600",
                        transition: "all 0.2s",
                        opacity: updating === user.email ? 0.5 : 1,
                        disabled: updating === user.email,
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                      disabled={updating === user.email}
                    >
                      {SVG_ICONS["trash"] && SVG_ICONS["trash"]({ size: 14, color: "#f87171" })}
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info Box */}
        <div
          style={{
            marginTop: "32px",
            padding: "16px",
            background: "rgba(168, 85, 247, 0.1)",
            border: "1px solid rgba(168, 85, 247, 0.3)",
            borderRadius: "8px",
            fontSize: "13px",
            color: "#d8b4fe",
            lineHeight: "1.6",
          }}
        >
          <strong>Users Management Note:</strong>
          <ul style={{ margin: "8px 0 0 20px", paddingLeft: "0" }}>
            <li>Ori toggle enables/disables Ori for individual users</li>
            <li>
              Demo toggle grants an App Review sign-in with a fixed code instead of an
              emailed one, and expires 30 days after it is switched on
            </li>
            <li>Can be turned on per-user even if globally disabled</li>
            <li>Users must accept Terms and Privacy to use the app fully</li>
            <li>Website and Role are optional user profile fields</li>
            <li>Registered date shows when user first joined</li>
          </ul>
        </div>
      </div>
    </div>
  );
});

export default UsersTab;
